import type { ColumnDef } from '@tanstack/react-table';
import { Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import {
    DynamicDeliveryBucket,
    MicrostoreColumnTemplateType,
    SiteContractType,
    type GetDynamicDeliveryPlanningRowsForDateQuery,
} from 'data/__generated__';
import { textColumn, numberColumn, multiSelectColumn, numberRangeColumn } from 'components/DataTable';
import { DetailFormStatusValue } from 'components/DetailsView';
import { colors } from 'constants/colors';
import { MICROSTORE_COLUMN_TEMPLATE_TYPES_OPTIONS } from 'pages/MicrostoreColumns/constants/template-types';
import { SITE_CONTRACT_TYPES_OPTIONS } from 'pages/Sites/constants/contract-types';

export type DynamicDeliveryRow =
    GetDynamicDeliveryPlanningRowsForDateQuery['getDynamicDeliveryPlanningRowsForDate'][number];

// Row enriched with the live "à livrer" flag derived from the form state, used for the section grouping.
export type PlanningTableRow = DynamicDeliveryRow & { isDeliver: boolean };

// Filter state of the planning table (client mode). The multi-select keys hold the columns' accessor values
// (zone name, operator fullname, motif label); the range keys hold { gte, lte } from the toolbar.
// `operators` targets the ASSIGNED deliverer (who actually delivers), not the zone referent.
export type DynamicDeliveryFilter = {
    searchValue?: string;
    zones?: string[];
    operators?: string[];
    templateTypes?: string[];
    contractTypes?: string[];
    flows?: string[];
    motifs?: string[];
    daysSinceLastDelivery?: { gte?: number; lte?: number };
    realFillRatePercent?: { gte?: number; lte?: number };
};

export type FilterOption = { value: string; label: string };

// Closed enums, so the options are hardcoded rather than collected from the day's rows (as zones and appros
// are): both values must stay filterable even on a day that only contains one of them.
const CONTRACT_TYPE_OPTIONS: FilterOption[] = Object.entries(SITE_CONTRACT_TYPES_OPTIONS).map(([value, { label }]) => ({
    value,
    label,
}));

export const FLOW_DYNAMIC = 'dynamique';
export const FLOW_FIXED = 'fixe';
const FLOW_OPTIONS: FilterOption[] = [
    { value: FLOW_DYNAMIC, label: 'Dynamique' },
    { value: FLOW_FIXED, label: 'Fixe' },
];

// Group key for rows without an assigned appro — also the droppableId of the "no appro" column in the tour
// list. Real appro ids are Mongo hex, so this never collides.
export const UNASSIGNED_DROPPABLE_ID = 'none';

export const approIdOf = (row: DynamicDeliveryRow): string => row.assignedDeliverer?._id ?? UNASSIGNED_DROPPABLE_ID;

export const sumMetrics = (rows: DynamicDeliveryRow[]): { kg: number; volume: number } => ({
    kg: rows.reduce((sum, row) => sum + (row.metrics?.kgToDeliver ?? 0), 0),
    volume: rows.reduce((sum, row) => sum + (row.metrics?.volumeCm3ToDeliver ?? 0), 0),
});

// Volume display (cm³): rounded, French thousands grouping. Shared by the summary cards and group headers.
export const formatVolume = (value: number): string => Math.round(value).toLocaleString('fr-FR');

// Regroupement label, shared by the table group header and the tour-list card so both name a rank identically.
export const regroupementRankLabel = (rank: number): string => (rank > 0 ? `#${rank}` : 'Hors classement');

export type PlanningAggregate = {
    deliverCount: number;
    potentialCount: number;
    deliver: { kg: number; volume: number };
    potential: { kg: number; volume: number };
    // Urgency = motif status 'critical' (SAME definition as the Motif column, via motifFor): a column is
    // urgent when it is Trop ancien (max days exceeded) or Critique (stock under critical threshold).
    // Manual additions are a human choice, not a field urgency — they are NOT counted here.
    urgent: {
        deliverTotal: number;
        deliverCritical: number; // motif « Critique »
        deliverTooOld: number; // motif « Trop ancien »
        potentialTotal: number;
    };
};

// Single source of truth for the planning counters: the summary cards (page) and the group headers
// (PlanningGroupRow) both read from here, so "à livrer / potentielles" counts and kg/volume can never diverge.
export const aggregatePlanningRows = (rows: readonly PlanningTableRow[]): PlanningAggregate => {
    const aggregate: PlanningAggregate = {
        deliverCount: 0,
        potentialCount: 0,
        deliver: { kg: 0, volume: 0 },
        potential: { kg: 0, volume: 0 },
        urgent: { deliverTotal: 0, deliverCritical: 0, deliverTooOld: 0, potentialTotal: 0 },
    };
    for (const row of rows) {
        if (row.isDeliver) aggregate.deliverCount += 1;
        else aggregate.potentialCount += 1;
        const side = row.isDeliver ? aggregate.deliver : aggregate.potential;
        side.kg += row.metrics?.kgToDeliver ?? 0;
        side.volume += row.metrics?.volumeCm3ToDeliver ?? 0;

        const motif = motifFor(row);
        if (motif?.status === 'critical') {
            if (row.isDeliver) {
                aggregate.urgent.deliverTotal += 1;
                if (motif.label === 'Critique') aggregate.urgent.deliverCritical += 1;
                if (motif.label === 'Trop ancien') aggregate.urgent.deliverTooOld += 1;
            } else {
                aggregate.urgent.potentialTotal += 1;
            }
        }
    }
    return aggregate;
};

// The day's stop position, stamped by the ranking (zone driving order) and overwritten by a drag. A row without
// one (activated by hand after the ranking) sorts last.
const tourOrderValue = (row: DynamicDeliveryRow): number => row.tourOrder ?? Number.MAX_SAFE_INTEGER;

// Single delivery-sequence comparator for the tour list, the map and the API tour builder.
export const compareTourPosition = (a: DynamicDeliveryRow, b: DynamicDeliveryRow): number =>
    tourOrderValue(a) - tourOrderValue(b);

export const coordsOf = (row: DynamicDeliveryRow): [number, number] | null => {
    const totem = row.totem;
    if (!totem || totem.latitude == null || totem.longitude == null) return null;
    return [totem.latitude, totem.longitude];
};

// Great-circle distance between two points (as the crow flies — no road routing).
export const haversineKm = (a: [number, number], b: [number, number]): number => {
    const toRad = (deg: number): number => (deg * Math.PI) / 180;
    const dLat = toRad(b[0] - a[0]);
    const dLng = toRad(b[1] - a[1]);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
    return 2 * 6371 * Math.asin(Math.sqrt(h));
};

// Preserves first-seen key order — every planning grouping (appro, rank, site, machine) relies on it.
export const groupByKey = <T,>(items: T[], keyOf: (item: T) => string): Map<string, T[]> => {
    const groups = new Map<string, T[]>();
    for (const item of items) {
        const key = keyOf(item);
        const list = groups.get(key);
        if (list) list.push(item);
        else groups.set(key, [item]);
    }
    return groups;
};

// One physical stop of a tour = one site (all its columns to deliver), in delivery order.
export type TourStop = { siteId: string; siteName: string; rows: PlanningTableRow[] };

// One stop = one site: shared by the stop sequence, the tour list blocks and the map markers.
export const groupRowsBySite = (rows: PlanningTableRow[]): TourStop[] =>
    [...groupByKey(rows, (row) => row.site?._id ?? row._id).entries()].map(([siteId, siteRows]) => ({
        siteId,
        siteName: siteRows[0].site?.name ?? 'Site inconnu',
        rows: siteRows,
    }));

// THE shared stop sequence per appro: the tour list numbers its rows and the map numbers its markers from
// this same structure, so both always show the same order and the same stop numbers.
export const buildTourStops = (deliverRows: PlanningTableRow[]): Map<string, TourStop[]> => {
    const stopsByAppro = new Map<string, TourStop[]>();
    for (const [approId, approRows] of groupByKey(deliverRows, approIdOf)) {
        // Regroupement-major ordering — the EXACT sequence the tour list displays (cards ordered by their
        // first row, rows ordered inside each card).
        const ordered = [...groupByKey(approRows, (row) => String(row.rank)).values()]
            .map((rankRows) => [...rankRows].sort(compareTourPosition))
            .sort((a, b) => compareTourPosition(a[0], b[0]))
            .flat();
        stopsByAppro.set(approId, groupRowsBySite(ordered));
    }
    return stopsByAppro;
};

// Tour length = sum of the legs between consecutive geolocated stops (only consumed by the estimate).
const tourDistanceKm = (stops: TourStop[]): number => {
    let distance = 0;
    let previous: [number, number] | null = null;
    for (const stop of stops) {
        const pos = coordsOf(stop.rows[0]);
        if (!pos) continue;
        if (previous) distance += haversineKm(previous, pos);
        previous = pos;
    }
    return distance;
};

// Feasibility heuristic (phase 1 — no routing API): road distance ≈ crow-flies × urban detour factor;
// duration = driving at an average urban speed + a fixed service time per stop and per column. Deliberate
// ±20% estimates whose only job is to surface infeasible tours; promote to Ops parameters once validated.
const ROAD_DETOUR_FACTOR = 1.35;
const AVERAGE_SPEED_KMH = 25;
const SERVICE_MINUTES_PER_STOP = 15;
const SERVICE_MINUTES_PER_COLUMN = 5;
const MAX_TOUR_MINUTES = 7 * 60;

export type TourEstimate = { roadKm: number; totalMinutes: number; isOverloaded: boolean };

export const estimateTour = (stops: TourStop[]): TourEstimate => {
    const roadKm = tourDistanceKm(stops) * ROAD_DETOUR_FACTOR;
    const columnCount = stops.reduce((sum, stop) => sum + stop.rows.length, 0);
    const totalMinutes =
        (roadKm / AVERAGE_SPEED_KMH) * 60 +
        stops.length * SERVICE_MINUTES_PER_STOP +
        columnCount * SERVICE_MINUTES_PER_COLUMN;
    return { roadKm, totalMinutes, isOverloaded: totalMinutes > MAX_TOUR_MINUTES };
};

export const TOUR_ESTIMATE_HINT = `Estimation : distance vol d'oiseau × ${ROAD_DETOUR_FACTOR} à ${AVERAGE_SPEED_KMH} km/h + ${SERVICE_MINUTES_PER_STOP} min/arrêt + ${SERVICE_MINUTES_PER_COLUMN} min/colonne — alerte au-delà de ${MAX_TOUR_MINUTES / 60}h`;

// A metric's status vs the row's resolved Ops thresholds. `null` value = no telemetry.
type RowThresholds = NonNullable<DynamicDeliveryRow['thresholds']>;
export type MetricStatus = 'critical' | 'required' | 'ok' | 'unknown';

export const fillStatus = (
    value: number | null | undefined,
    thresholds: RowThresholds | null | undefined,
): MetricStatus => {
    if (value == null || !thresholds) return 'unknown';
    if (value <= thresholds.criticalPassPercent) return 'critical';
    if (value <= thresholds.requiredPassPercent) return 'required';
    return 'ok';
};

export const daysStatus = (
    value: number | null | undefined,
    thresholds: RowThresholds | null | undefined,
): MetricStatus => {
    const max = thresholds?.maxDaysWithoutDelivery;
    if (value == null || max == null) return 'unknown';
    // ≥ maxDaysWithoutDelivery → urgent (red). Single Ops threshold, no intermediate tier.
    return value >= max ? 'critical' : 'ok';
};

// Tooltips exposing the exact Ops threshold a cell is judged against — including the resolved (type × contract)
// combo, so a wrong site contractType (e.g. a deposit site reading Subscription thresholds) is visible at a glance.
const NO_THRESHOLD = 'Aucun seuil Ops configuré pour ce type de colonne × contrat';
const CONTRACT_LABEL: Record<string, string> = { Subscription: 'Abonnement', FreeDeposit: 'Dépôt gratuit' };
const comboLabel = (t: RowThresholds): string =>
    `${t.templateType} · ${CONTRACT_LABEL[t.contractType] ?? t.contractType}`;
const stockHint = (t: RowThresholds | null | undefined): string =>
    t ? `${comboLabel(t)} — Critique ≤ ${t.criticalPassPercent}% · Requis ≤ ${t.requiredPassPercent}%` : NO_THRESHOLD;
const daysHint = (t: RowThresholds | null | undefined): string =>
    t?.maxDaysWithoutDelivery != null ? `${comboLabel(t)} — Urgent ≥ ${t.maxDaysWithoutDelivery} j` : NO_THRESHOLD;

const qualityStatus = (
    value: number | null | undefined,
    thresholds: RowThresholds | null | undefined,
): MetricStatus => {
    const max = thresholds?.qualityPassPercent;
    if (value == null || max == null) return 'unknown';
    return value > max ? 'critical' : 'ok';
};
const qualityHint = (t: RowThresholds | null | undefined): string =>
    t?.qualityPassPercent != null ? `${comboLabel(t)} — Slots vides max ${t.qualityPassPercent}%` : NO_THRESHOLD;

const RankCell = ({ row }: { row: DynamicDeliveryRow }) => {
    if (!row.rank) return <Muted>—</Muted>; // rank 0 = safety net
    const isHead = !row.parentDynamicDeliveryPlanningId;
    return isHead ? <HeadRank>{row.finalRank}</HeadRank> : <ChildRank>{row.finalRank}</ChildRank>;
};

const MOTIF_BY_BUCKET: Record<DynamicDeliveryBucket, { label: string; status: MetricStatus }> = {
    [DynamicDeliveryBucket.MaxDays]: { label: 'Trop ancien', status: 'critical' },
    [DynamicDeliveryBucket.Critical]: { label: 'Critique', status: 'critical' },
    [DynamicDeliveryBucket.Required]: { label: 'Requis', status: 'required' },
};

export const motifFor = (row: DynamicDeliveryRow): { label: string; status: MetricStatus } | null => {
    // A human choice is not a field urgency: Manuel (added by hand) reads as 'required', never 'critical',
    // so it doesn't inflate the Urgences card; Retiré (withdrawn by hand) is neutral.
    if (row.isManual)
        return { label: row.isActive ? 'Manuel' : 'Retiré', status: row.isActive ? 'required' : 'unknown' };
    if (row.bucket) return MOTIF_BY_BUCKET[row.bucket];
    if (row.parentDynamicDeliveryPlanningId) return { label: 'Regroupement', status: 'ok' };
    return null;
};

const MotifCell = ({ row }: { row: DynamicDeliveryRow }) => {
    const motif = motifFor(row);
    if (!motif) return <Muted>—</Muted>;
    return <StatusText $status={motif.status}>{motif.label}</StatusText>;
};

/**
 * The shared `DynamicDeliveryTag` bolt, but with a native `title` instead of its Radix tooltip: this table
 * renders hundreds of them, up to a thousand expanded, and each tooltip mounts its own provider.
 */
const DynamicBolt = () => (
    <BoltWrap title="Colonne dynamique" aria-label="Colonne dynamique">
        <Zap size={13} fill={colors.violet} strokeWidth={0} />
    </BoltWrap>
);

const SiteCell = ({ row }: { row: DynamicDeliveryRow }) => {
    const site = row.site;
    if (!site?._id) return <Muted>—</Muted>;
    return <RowLink to={`/site/${site._id}`}>{site.name}</RowLink>;
};

const ColumnCell = ({ row }: { row: DynamicDeliveryRow }) => {
    const column = row.column;
    const isChild = !!row.parentDynamicDeliveryPlanningId;
    if (!column?._id) return <Muted>—</Muted>;
    return (
        <ColumnNameCell $isChild={isChild}>
            {isChild ? <ChildIndicator title="Colonne groupée avec une tête de groupe">↳</ChildIndicator> : null}
            <RowLink to={`/microstore-column/${column._id}`}>{column.name}</RowLink>
            {column.useDynamicDelivery && <DynamicBolt />}
        </ColumnNameCell>
    );
};

const ZoneChipCell = ({ row }: { row: DynamicDeliveryRow }) => {
    const zone = row.deliveryZone;
    if (!zone) return <Muted>—</Muted>;
    return (
        <ZoneChip>
            <ZoneSwatch style={{ backgroundColor: zone.color }} />
            {zone.name}
        </ZoneChip>
    );
};

const PersonCell = ({ name }: { name: string | null | undefined }) => (name ? <span>{name}</span> : <Muted>—</Muted>);

const PercentCell = ({ value }: { value: number | null | undefined }) => {
    if (value === null || value === undefined) return <Muted>—</Muted>;
    return <span>{value.toFixed(0)}%</span>;
};

const MetricCell = ({
    value,
    status,
    title,
}: {
    value: number | null | undefined;
    status: MetricStatus;
    title?: string;
}) => {
    if (value === null || value === undefined) return <Muted title={title}>—</Muted>;
    return (
        <StatusText $status={status} title={title}>
            {value.toFixed(0)}%
        </StatusText>
    );
};

const DaysCell = ({ row }: { row: DynamicDeliveryRow }) => {
    const value = row.metrics?.daysSinceLastDelivery;
    const title = daysHint(row.thresholds);
    if (value === null || value === undefined) return <Muted title={title}>—</Muted>;
    return (
        <StatusText $status={daysStatus(value, row.thresholds)} title={title}>
            {value}
        </StatusText>
    );
};

const NumberCell = ({ value, suffix }: { value: number | null | undefined; suffix?: string }) => {
    if (value === null || value === undefined) return <Muted>—</Muted>;
    return (
        <span>
            {value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}
            {suffix ? ` ${suffix}` : ''}
        </span>
    );
};

// Motif filter options: every label motifFor can produce (buckets + manual states + child rows + none).
const MOTIF_FILTER_OPTIONS: FilterOption[] = [
    { value: 'Trop ancien', label: 'Trop ancien' },
    { value: 'Critique', label: 'Critique' },
    { value: 'Requis', label: 'Requis' },
    { value: 'Manuel', label: 'Manuel' },
    { value: 'Regroupement', label: 'Regroupement' },
    { value: 'Retiré', label: 'Retiré' },
    { value: '—', label: 'Sans motif' },
];

// The zone/operator filter options are derived from the day's rows, so the columns are built per render
// (memoised by the page) instead of exported as a static array.
export const buildDynamicDeliveryColumns = ({
    zoneOptions,
    operatorOptions,
    templateTypeOptions,
}: {
    zoneOptions: FilterOption[];
    operatorOptions: FilterOption[];
    templateTypeOptions: FilterOption[];
}): ColumnDef<PlanningTableRow, unknown>[] => [
    {
        ...numberColumn<PlanningTableRow, DynamicDeliveryFilter>({
            id: 'rank',
            accessorFn: (row) => row.rank * 1000 + row.secondaryRank,
            header: 'Rang',
            defaultVisible: true,
            cell: ({ row }) => <RankCell row={row.original} />,
        }),
        // Group rows (regroupement, appro) inherit the lowest rank of their leaves — sort by Rang then becomes deterministic.
        aggregationFn: 'min',
    },
    textColumn<PlanningTableRow, DynamicDeliveryFilter>({
        id: 'siteName',
        accessorFn: (row) => row.site?.name ?? '',
        header: 'Site',
        defaultVisible: true,
        cell: ({ row }) => <SiteCell row={row.original} />,
    }),
    textColumn<PlanningTableRow, DynamicDeliveryFilter>({
        id: 'machineCode',
        accessorFn: (row) => row.column?.machineCode ?? '',
        header: 'Code machine',
        defaultVisible: false,
    }),
    textColumn<PlanningTableRow, DynamicDeliveryFilter>({
        id: 'columnName',
        accessorFn: (row) => row.column?.name ?? '',
        header: 'Colonne',
        defaultVisible: true,
        cell: ({ row }) => <ColumnCell row={row.original} />,
    }),
    multiSelectColumn<PlanningTableRow, DynamicDeliveryFilter>({
        id: 'templateType',
        accessorFn: (row) => row.column?.type ?? '',
        header: 'Type colonne',
        defaultVisible: false,
        filterKey: 'templateTypes',
        options: templateTypeOptions,
        renderOption: (option) => (
            <DetailFormStatusValue
                state={option.value as MicrostoreColumnTemplateType}
                options={MICROSTORE_COLUMN_TEMPLATE_TYPES_OPTIONS}
            />
        ),
        cell: ({ row }) =>
            row.original.column?.type ? (
                <DetailFormStatusValue
                    state={row.original.column.type}
                    options={MICROSTORE_COLUMN_TEMPLATE_TYPES_OPTIONS}
                />
            ) : (
                <Muted>—</Muted>
            ),
    }),
    multiSelectColumn<PlanningTableRow, DynamicDeliveryFilter>({
        id: 'contractType',
        accessorFn: (row) => row.site?.contractType ?? '',
        header: 'Contrat',
        defaultVisible: false,
        filterKey: 'contractTypes',
        options: CONTRACT_TYPE_OPTIONS,
        renderOption: (option) => (
            <DetailFormStatusValue state={option.value as SiteContractType} options={SITE_CONTRACT_TYPES_OPTIONS} />
        ),
        cell: ({ row }) =>
            row.original.site?.contractType ? (
                <DetailFormStatusValue state={row.original.site.contractType} options={SITE_CONTRACT_TYPES_OPTIONS} />
            ) : (
                <Muted>—</Muted>
            ),
    }),
    multiSelectColumn<PlanningTableRow, DynamicDeliveryFilter>({
        id: 'flow',
        accessorFn: (row) => (row.column?.useDynamicDelivery ? FLOW_DYNAMIC : FLOW_FIXED),
        header: 'Flux',
        defaultVisible: false,
        filterKey: 'flows',
        options: FLOW_OPTIONS,
        cell: ({ row }) => <span>{row.original.column?.useDynamicDelivery ? 'Dynamique' : 'Fixe'}</span>,
    }),
    multiSelectColumn<PlanningTableRow, DynamicDeliveryFilter>({
        id: 'zone',
        accessorFn: (row) => row.deliveryZone?.name ?? '',
        header: 'Zone',
        defaultVisible: true,
        filterKey: 'zones',
        options: zoneOptions,
        cell: ({ row }) => <ZoneChipCell row={row.original} />,
    }),
    textColumn<PlanningTableRow, DynamicDeliveryFilter>({
        id: 'referent',
        accessorFn: (row) => row.referentDeliverer?.profile.fullname ?? '',
        header: 'Appro référent',
        defaultVisible: false,
        cell: ({ row }) => <PersonCell name={row.original.referentDeliverer?.profile.fullname} />,
    }),
    // The deliverer filter targets the ASSIGNED deliverer (who actually does the delivery), not the referent.
    multiSelectColumn<PlanningTableRow, DynamicDeliveryFilter>({
        id: 'operator',
        accessorFn: (row) => row.assignedDeliverer?.profile.fullname ?? '',
        header: 'Opérateur',
        defaultVisible: false,
        filterKey: 'operators',
        options: operatorOptions,
        cell: ({ row }) => <PersonCell name={row.original.assignedDeliverer?.profile.fullname} />,
    }),
    {
        ...numberRangeColumn<PlanningTableRow, DynamicDeliveryFilter>({
            id: 'realFillRatePercent',
            // Unknown telemetry ("—") sorts in its own band just above full (101) — never as empty (0) nor
            // full (100). Side effect on the range filter: « stock ≤ X % » naturally excludes unknowns.
            accessorFn: (row) => row.metrics?.realFillRatePercent ?? 101,
            header: 'Stock réel %',
            defaultVisible: true,
            filterKey: 'realFillRatePercent',
            cell: ({ row }) => (
                <MetricCell
                    value={row.original.metrics?.realFillRatePercent}
                    status={fillStatus(row.original.metrics?.realFillRatePercent, row.original.thresholds)}
                    title={stockHint(row.original.thresholds)}
                />
            ),
        }),
        // Worst-stocked groups should surface when sorting asc → take the lowest value inside the group.
        aggregationFn: 'min',
    },
    {
        ...numberColumn<PlanningTableRow, DynamicDeliveryFilter>({
            id: 'transactionalFillRatePercent',
            accessorFn: (row) => row.metrics?.transactionalFillRatePercent ?? 100,
            header: 'Stock trans. %',
            defaultVisible: true,
            cell: ({ row }) => (
                <MetricCell
                    value={row.original.metrics?.transactionalFillRatePercent}
                    status={fillStatus(row.original.metrics?.transactionalFillRatePercent, row.original.thresholds)}
                    title={stockHint(row.original.thresholds)}
                />
            ),
        }),
        aggregationFn: 'min',
    },
    {
        ...numberColumn<PlanningTableRow, DynamicDeliveryFilter>({
            id: 'trayFillRatePercent',
            accessorFn: (row) => row.metrics?.trayFillRatePercent ?? -1,
            header: 'Bac café %',
            defaultVisible: false,
            cell: ({ row }) => <PercentCell value={row.original.metrics?.trayFillRatePercent ?? null} />,
        }),
        aggregationFn: 'min',
    },
    {
        ...numberColumn<PlanningTableRow, DynamicDeliveryFilter>({
            id: 'missingReferencesPercent',
            accessorFn: (row) => row.metrics?.missingReferencesPercent ?? 0,
            header: 'Réf. manq. %',
            defaultVisible: true,
            cell: ({ row }) =>
                row.original.metrics?.realFillRatePercent == null ? (
                    <Muted>—</Muted>
                ) : (
                    <MetricCell
                        value={row.original.metrics?.missingReferencesPercent}
                        status={qualityStatus(row.original.metrics?.missingReferencesPercent, row.original.thresholds)}
                        title={qualityHint(row.original.thresholds)}
                    />
                ),
        }),
        // Higher = more refs missing inside the group → take the max so sort desc surfaces the worst groups first.
        aggregationFn: 'max',
    },
    {
        ...numberRangeColumn<PlanningTableRow, DynamicDeliveryFilter>({
            id: 'daysSinceLastDelivery',
            // Unknown (-1) sorts below 0 and is excluded as soon as a min is set on the range filter.
            accessorFn: (row) => row.metrics?.daysSinceLastDelivery ?? -1,
            header: 'Jours dep. liv.',
            defaultVisible: true,
            filterKey: 'daysSinceLastDelivery',
            cell: ({ row }) => <DaysCell row={row.original} />,
        }),
        // Group urgency reflects the column delivered the longest time ago → max.
        aggregationFn: 'max',
    },
    {
        ...numberColumn<PlanningTableRow, DynamicDeliveryFilter>({
            id: 'kgToDeliver',
            accessorFn: (row) => row.metrics?.kgToDeliver ?? 0,
            header: 'Kg',
            defaultVisible: false,
            cell: ({ row }) => <NumberCell value={row.original.metrics?.kgToDeliver} suffix="kg" />,
        }),
        aggregationFn: 'sum',
    },
    {
        ...numberColumn<PlanningTableRow, DynamicDeliveryFilter>({
            id: 'volumeCm3ToDeliver',
            accessorFn: (row) => row.metrics?.volumeCm3ToDeliver ?? 0,
            header: 'Volume',
            defaultVisible: false,
            cell: ({ row }) => <NumberCell value={row.original.metrics?.volumeCm3ToDeliver} suffix="cm³" />,
        }),
        aggregationFn: 'sum',
    },
    textColumn<PlanningTableRow, DynamicDeliveryFilter>({
        id: 'parentColumnName',
        accessorFn: (row) => row.parentColumn?.name ?? '',
        header: 'Tête de groupe',
        defaultVisible: false,
    }),
    {
        ...multiSelectColumn<PlanningTableRow, DynamicDeliveryFilter>({
            id: 'motif',
            accessorFn: (row) => motifFor(row)?.label ?? '—',
            header: 'Motif',
            defaultVisible: true,
            filterKey: 'motifs',
            options: MOTIF_FILTER_OPTIONS,
            cell: ({ row }) => <MotifCell row={row.original} />,
        }),
        enableSorting: false,
    },
    // Hidden columns used only for grouping (kept out of the column-visibility menu).
    {
        id: 'section',
        accessorFn: (row) => (row.isDeliver ? 'deliver' : 'potential'),
        header: 'Section',
        enableHiding: false,
        meta: { defaultVisible: false },
    },
    {
        id: 'operatorGroup',
        accessorFn: (row) => row.assignedDeliverer?._id ?? '',
        header: 'Opérateur',
        enableHiding: false,
        meta: { defaultVisible: false },
    },
    {
        id: 'regroupement',
        accessorFn: (row) => String(row.rank),
        header: 'Regroupement',
        enableHiding: false,
        meta: { defaultVisible: false },
    },
    {
        id: 'siteGroup',
        accessorFn: (row) => row.site?._id ?? '',
        header: 'Site',
        enableHiding: false,
        meta: { defaultVisible: false },
    },
];

const RowLink = styled(Link)`
    color: ${({ theme }) => theme.textColor};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const Muted = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
`;

export const STATUS_TOKEN: Record<MetricStatus, 'errorColor' | 'warningColor' | 'successColor' | 'infoTextColor'> = {
    critical: 'errorColor',
    required: 'warningColor',
    ok: 'successColor',
    unknown: 'infoTextColor',
};

const StatusText = styled.span<{ $status: MetricStatus }>`
    color: ${({ theme, $status }) => theme[STATUS_TOKEN[$status]]};
    font-weight: ${({ $status }) => ($status === 'critical' || $status === 'required' ? 600 : 400)};
`;

const ZoneChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
`;

const ZoneSwatch = styled.span`
    width: 12px;
    height: 12px;
    border-radius: 3px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    flex-shrink: 0;
`;

const BoltWrap = styled.span`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    color: ${colors.violet};
`;

const ColumnNameCell = styled.span<{ $isChild: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding-left: ${({ $isChild }) => ($isChild ? '12px' : '0')};
`;

const ChildIndicator = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 16px;
    line-height: 1;
`;

const HeadRank = styled.span`
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
`;

const ChildRank = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
`;
